import { useMemo } from 'react'

import { FormMembershipListDocument } from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'
import { FORMS_ELEMENT_TYPE } from 'modules/forms/constants/elementType'
import { MEMBERSHIP_STATUS } from 'modules/forms/constants/membershipStatus'

export type FormMembershipItem = {
  membershipId: string
  materializedFormId: string
  status: string
  formType: string | null | undefined
  form:
    | {
        id: string
        title: string
        elements: Array<{ id: string; type: string }>
      }
    | null
    | undefined
  questionsCount: number
  estimatedMinutes: number
}

export type UseFormMembershipsResult = {
  memberships: FormMembershipItem[]
  loading: boolean
  refetch: () => void
}

export const useFormMemberships = ({
  respondentId,
  resourceType,
  formType,
  trainingId,
  onlyPending = false,
  skip = false,
}: {
  respondentId: string
  resourceType: string
  formType?: string[]
  trainingId?: string
  onlyPending?: boolean
  skip?: boolean
}): UseFormMembershipsResult => {
  const { data, loading, refetch } = useQuery(FormMembershipListDocument, {
    variables: {
      respondentId,
      resourceType,
      formType,
      trainingId,
    },
    skip: skip || !respondentId,
    fetchPolicy: 'cache-and-network',
  })

  const allMemberships = useMemo(() => {
    return data?.forms?.memberships ?? []
  }, [data])

  const memberships = useMemo<FormMembershipItem[]>(() => {
    const filtered = onlyPending
      ? allMemberships.filter((m) => m.status === MEMBERSHIP_STATUS.Pending)
      : allMemberships
    return filtered.map((m) => {
      const elCount =
        m.materializedForm?.form?.elements?.filter((el) => el.type !== FORMS_ELEMENT_TYPE.Section)
          ?.length ?? 0
      const estimatedMinutes = Math.max(1, Math.ceil(elCount / 2))
      return {
        membershipId: m.id,
        materializedFormId: m.materializedForm?.id ?? '',
        status: m.status,
        formType: m.materializedForm?.formType,
        form: m.materializedForm?.form ?? null,
        questionsCount: elCount,
        estimatedMinutes,
      }
    })
  }, [allMemberships, onlyPending])

  return {
    memberships,
    loading,
    refetch,
  }
}
